/**
 * The console kit — what this product composes on top of the shared design system.
 *
 * These are the pieces that make a *marketplace ops console* rather than a generic admin: money
 * that renders one way everywhere, an action that explains why it is disabled, and a confirm
 * dialog that states consequences and makes you type a phrase before anything irreversible moves.
 */
import { useState, useEffect, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { Icon, Button, Card, Chip, Kicker, StatusChip, type ButtonProps } from './primitives';
import { Input } from './forms';
import { Modal, UnpaddedBodyContext } from './feedback';
import { formatInr, type FormatOptions } from '../lib/money';
import { can, describeNeed, type Scope } from '../lib/rbac';
import { authState, subscribeToAuth } from '../lib/auth/session';

/* ────────────────────────────  Money  ──────────────────────────── */

export interface MoneyProps {
  /** Integer paise. Always. */
  paise: number;
  showPaise?: boolean;
  tone?: 'default' | 'muted' | 'good';
  bold?: boolean;
  style?: CSSProperties;
}

/**
 * The single way money reaches the screen. Right-aligned tabular numerals, because a money column
 * is read by comparing digits vertically — and a negative renders with a real minus, not a hyphen.
 */
export function Money({ paise, showPaise, tone = 'default', bold, style }: MoneyProps) {
  const opts: FormatOptions = showPaise ? { paise: true } : {};
  const color =
    tone === 'muted'
      ? 'var(--c-ink3)'
      : paise < 0
        ? 'var(--c-error-text)'
        : tone === 'good'
          ? 'var(--c-success-text)'
          : 'var(--c-ink)';
  return (
    <span
      className="tabular mono-num"
      style={{
        display: 'block',
        textAlign: 'right',
        color,
        fontWeight: bold ? 'var(--fw-semibold)' : 'inherit',
        ...style,
      }}
    >
      {formatInr(paise, opts)}
    </span>
  );
}

/* ────────────────────────────  ScopeAction  ──────────────────────────── */

export interface ScopeActionProps extends Omit<ButtonProps, 'disabled'> {
  scope: Scope;
  need: Scope | readonly Scope[];
  disabled?: boolean;
}

/**
 * A button the operator may not be allowed to press. When they are not, it renders disabled and
 * **says which scope is required** — a greyed control with no explanation reads as broken software
 * rather than as a boundary.
 *
 * Reminder, because it matters: this is UX. The server rejects the call regardless.
 */
export function ScopeAction({ scope, need, children, disabled, ...rest }: ScopeActionProps) {
  const allowed = can(scope, need);
  return (
    <span data-tip={allowed ? undefined : `Requires scope: ${describeNeed(need)}`} style={{ display: 'inline-flex' }}>
      <Button {...rest} disabled={disabled || !allowed}>
        {children}
      </Button>
    </span>
  );
}

/* ────────────────────────────  Section  ──────────────────────────── */

export interface SectionProps {
  title: string;
  note?: string;
  actions?: ReactNode;
  children?: ReactNode;
  /** `false` removes the body padding, for a Section wrapping a full-bleed table. */
  pad?: boolean;
  style?: CSSProperties;
}

export function Section({ title, note, actions, children, pad = true, style }: SectionProps) {
  return (
    <Card variant="flat" padding="0" style={{ overflow: 'hidden', ...style }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-md)',
          padding: 'var(--sp-md) var(--sp-lg)',
          borderBottom: '1px solid var(--c-border)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-admin-card-heading)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--c-ink)',
            }}
          >
            {title}
          </div>
          {note && <div style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{note}</div>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>{actions}</div>
      </div>
      <div style={{ padding: pad ? 'var(--sp-lg)' : 0, display: 'grid', gap: 'var(--sp-md)' }}>
        <UnpaddedBodyContext.Provider value={!pad}>{children}</UnpaddedBodyContext.Provider>
      </div>
    </Card>
  );
}

/* ────────────────────────────  StatTile  ──────────────────────────── */

export interface StatTileProps {
  label: string;
  value: ReactNode;
  note?: string;
  tone?: 'default' | 'error';
  onClick?: () => void;
  style?: CSSProperties;
}

/**
 * Every tile is an actionable queue count or a health signal that clicks through. There are no
 * vanity KPIs in this console — a number nobody can act on is decoration.
 */
export function StatTile({ label, value, note, tone = 'default', onClick, style }: StatTileProps) {
  const colour = tone === 'error' ? 'var(--c-error-text)' : 'var(--c-ink)';
  return (
    <Card variant="flat" padding="var(--sp-lg)" onClick={onClick} style={{ display: 'grid', gap: 2, ...style }}>
      <Kicker>{label}</Kicker>
      <div
        className="tabular"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 26,
          lineHeight: '32px',
          fontWeight: 'var(--fw-bold)',
          color: colour,
        }}
      >
        {value}
      </div>
      {note && (
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: tone === 'error' ? colour : 'var(--c-ink3)' }}>
          {note}
        </span>
      )}
    </Card>
  );
}

/* ────────────────────────────  FilterBar  ──────────────────────────── */

export interface FilterTab {
  key: string;
  label: string;
}

export interface FilterBarProps {
  q?: string;
  onQ?: (value: string) => void;
  placeholder?: string;
  tabs?: readonly FilterTab[];
  tab?: string;
  onTab?: (key: string) => void;
  extra?: ReactNode;
  right?: ReactNode;
  searchId?: string;
  style?: CSSProperties;
}

/** The standing header of every queue. `/` focuses the search — see useQueue. */
export function FilterBar({
  q,
  onQ,
  placeholder = 'Filter — press /',
  tabs,
  tab,
  onTab,
  extra,
  right,
  searchId = 'admin-search',
  style,
}: FilterBarProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', flexWrap: 'wrap', ...style }}>
      {/* A search box only where the screen filters on it — a box that takes typing and narrows
          nothing reads as "no match" when the row is right there. */}
      {onQ && (
        <div style={{ flex: '0 1 300px' }}>
          <Input id={searchId} density="admin" icon="search" value={q} onChange={onQ} placeholder={placeholder} />
        </div>
      )}
      {tabs?.map((t) => (
        <Chip key={t.key} variant="filter" label={t.label} selected={tab === t.key} onClick={() => onTab?.(t.key)} />
      ))}
      {extra}
      <span style={{ flex: 1 }} />
      {right}
    </div>
  );
}

/* ────────────────────────────  Freshness  ──────────────────────────── */

/** Live-ish queues say how stale they are. Past 60s the chip warns rather than quietly lying. */
export function Freshness({ seconds, onRefresh }: { seconds: number; onRefresh?: () => void }) {
  const stale = seconds > 60;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-xs)',
        fontSize: 'var(--fs-admin-colhead)',
        color: stale ? 'var(--c-warn-text)' : 'var(--c-ink3)',
      }}
    >
      <Icon name="refresh-cw" size={12} />
      Data as of {seconds}s ago
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          style={{
            border: 'none',
            background: 'none',
            color: 'var(--c-saffron-text)',
            cursor: 'pointer',
            font: 'inherit',
            padding: '0 0 0 4px',
          }}
        >
          Refresh
        </button>
      )}
    </span>
  );
}

/* ────────────────────────────  Consequences  ──────────────────────────── */

/** What a commit actually does, in plain sentences, before it happens. */
/* ────────────────────────────  CountdownChip  ──────────────────────────── */

/** Minutes to a deadline as the board says them: "42m", "3h 5m", "2d"; a passed one reads "−4m". */
export function countdownText(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const abs = Math.abs(minutes);
  if (abs < 60) return `${sign}${abs}m`;
  if (abs < 60 * 48) return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
  return `${sign}${Math.floor(abs / 1440)}d`;
}

/** Whole minutes to `until`, re-read once a minute; `now` pins the clock and stops the ticking. */
function useMinutesTo(until: string, now: number | undefined): number {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (now !== undefined) return;
    const id = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [now]);
  return Math.round((Date.parse(until) - (now ?? tick)) / 60_000);
}

/** Inside the urgency window, or past the deadline; with no window, only past it. */
export function isUrgent(minutes: number, urgentWithinMinutes: number | undefined): boolean {
  return minutes <= 0 || (urgentWithinMinutes !== undefined && minutes <= urgentWithinMinutes);
}

/**
 * A live countdown to `until` (docs/05-admin/03 §6.14): an acceptance TTL, a hold expiry, an
 * issue window's end. It re-reads the clock once a minute, so the polite live region speaks at
 * minute granularity and never per second. Inside `urgentWithinMinutes` (the
 * `dispatch.urgent_window_minutes` setting, via `useUrgentWindowMinutes`) and once the deadline
 * has passed it takes the error colour, §5.6's urgency treatment. Without a window it never
 * flips: the line is ops data, and the chip does not guess it. `now` pins the clock for tests.
 */
export function CountdownChip({
  until,
  label,
  urgentWithinMinutes,
  now,
}: {
  until: string;
  label: string;
  urgentWithinMinutes?: number;
  now?: number;
}) {
  const minutes = useMinutesTo(until, now);
  const text = countdownText(minutes);
  const urgent = isUrgent(minutes, urgentWithinMinutes);
  return (
    <span role="timer" aria-live="polite" aria-label={`${label}: ${minutes <= 0 ? `passed ${text}` : `${text} left`}`}>
      <StatusChip tone={urgent ? 'error' : 'neutral'} icon="clock" label={`${label} ${text}`} />
    </span>
  );
}

const BANNER: Record<'info' | 'warn' | 'error', { icon: 'info' | 'triangle-alert' | 'circle-alert'; tone: string }> = {
  info: { icon: 'info', tone: 'info' },
  warn: { icon: 'triangle-alert', tone: 'warn' },
  error: { icon: 'circle-alert', tone: 'error' },
};

/**
 * A page-level condition (docs/05-admin/03 §6.8): a kill switch off, an authorization expiring, a
 * backlog building. Toasts report an outcome and leave; a banner states a standing fact and stays
 * while the fact holds, so the screen decides when to draw it. An error is announced at once
 * (`role="alert"`); info and warn wait their turn (`role="status"`). The kind is named by its
 * icon as well as its colour.
 */
export function Banner({
  kind,
  title,
  children,
  action,
}: {
  kind: 'info' | 'warn' | 'error';
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const { icon, tone } = BANNER[kind];
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-sm)',
        padding: 'var(--sp-sm) var(--sp-md)',
        background: `var(--c-${tone}-bg)`,
        color: `var(--c-${tone}-text)`,
        borderLeft: `3px solid var(--c-${tone})`,
        fontSize: 'var(--fs-admin-cell)',
      }}
    >
      <Icon name={icon} size={16} />
      <span style={{ flex: 1 }}>
        {title && <strong style={{ fontWeight: 'var(--fw-semibold)' }}>{title} </strong>}
        {children}
      </span>
      {action}
    </div>
  );
}

/**
 * The console's emergency banner (docs/05-admin/03 §5.6): full width, error background, on the
 * detail of a booking inside the urgency window of a deadline, or past it. Outside the window it
 * draws nothing, and it appears on its own when the minute clock crosses the line. The countdown
 * inside is a `CountdownChip`, so a screen never hand-rolls either half.
 */
export function UrgencyBanner({
  until,
  label,
  urgentWithinMinutes,
  children,
  now,
}: {
  until: string;
  label: string;
  urgentWithinMinutes?: number;
  children: ReactNode;
  now?: number;
}) {
  const minutes = useMinutesTo(until, now);
  if (!isUrgent(minutes, urgentWithinMinutes)) return null;
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-sm)',
        padding: 'var(--sp-sm) var(--sp-md)',
        background: 'var(--c-error-bg)',
        color: 'var(--c-error-text)',
        borderLeft: '3px solid var(--c-error)',
        fontSize: 'var(--fs-admin-cell)',
      }}
    >
      <Icon name="triangle-alert" size={16} />
      <span style={{ flex: 1 }}>{children}</span>
      <CountdownChip until={until} label={label} urgentWithinMinutes={urgentWithinMinutes} now={now} />
    </div>
  );
}

/** The heading over a server refusal shown in place. */
export const REFUSED = 'The server refused this';

export function Consequences({
  items,
  tone = 'warn',
  kicker = 'What this does when you commit',
}: {
  items: readonly string[];
  tone?: 'warn' | 'error';
  /** The heading; a server refusal is not a consequence, so it names itself. */
  kicker?: string;
}) {
  const fg = tone === 'error' ? 'var(--c-error-text)' : 'var(--c-warn-text)';
  return (
    <div
      style={{
        background: tone === 'error' ? 'var(--c-error-bg)' : 'var(--c-warn-bg)',
        border: `1px solid ${tone === 'error' ? 'var(--c-error)' : 'var(--c-warn)'}`,
        borderRadius: 'var(--r-tile)',
        padding: 'var(--sp-md)',
      }}
    >
      <Kicker style={{ color: fg }}>{kicker}</Kicker>
      <ul
        style={{
          margin: '6px 0 0',
          paddingLeft: 18,
          display: 'grid',
          gap: 4,
          fontSize: 'var(--fs-admin-cell)',
          color: fg,
        }}
      >
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

/* ────────────────────────────  ConfirmAction  ──────────────────────────── */

export interface ConfirmActionProps {
  open: boolean;
  title: string;
  /** The phrase the operator must type. Friction is the feature. */
  phrase: string;
  consequences: readonly string[];
  tone?: 'warn' | 'error';
  /**
   * What must be done before this can succeed, when the screen already knows the server would
   * refuse. Named in the dialog and the confirm stays shut — friction that ends in a 409 teaches
   * nothing.
   */
  blocked?: readonly string[];
  /** `true` for a generic required reason, or a string to label it specifically. */
  reason?: boolean | string;
  /**
   * Ready-made reasons (admin-managed rows, e.g. KYC reject templates). A click copies the body
   * into the reason field, where it can still be edited — never pre-selected.
   */
  quickPicks?: readonly { label: string; body: string }[];
  children?: ReactNode;
  actor?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * The safety component. Every money and public-copy action passes through here.
 *
 * Deliberately NOT keyboard-shortcuttable and never pre-filled: the operator has to read the
 * consequences, type the phrase, and give a reason that lands in the audit log with their name.
 */
export function ConfirmAction({
  open,
  title,
  phrase,
  consequences,
  tone = 'warn',
  blocked = [],
  reason,
  quickPicks = [],
  children,
  actor,
  confirmLabel = 'Commit',
  onCancel,
  onConfirm,
}: ConfirmActionProps) {
  const [typed, setTyped] = useState('');
  const [why, setWhy] = useState('');
  // Whoever is signed in, from the session — the line promises the audit log names this person,
  // so it may never name anybody else.
  const auth = useSyncExternalStore(subscribeToAuth, authState);
  const signedIn = auth.status === 'signed-in' ? auth.session.email : undefined;
  const who = actor ?? signedIn;

  useEffect(() => {
    if (open) {
      setTyped('');
      setWhy('');
    }
  }, [open]);

  if (!open) return null;

  const needsReason = Boolean(reason);
  const ready =
    blocked.length === 0 && typed.trim() === phrase && (!needsReason || why.trim().length > 3);

  return (
    <Modal
      title={title}
      width={560}
      onClose={onCancel}
      actions={
        <>
          <Button size="md" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="md"
            variant={tone === 'error' ? 'destructive' : 'primary'}
            disabled={!ready}
            onClick={() => onConfirm(why)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {children}
        {blocked.length > 0 && (
          <Consequences items={blocked.map((b) => `Not yet — first add ${b}.`)} tone="error" />
        )}
        <Consequences items={consequences} tone={tone} />
        {needsReason && quickPicks.length > 0 && (
          <div role="group" aria-label="Quick-pick reasons" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-xs)' }}>
            {quickPicks.map((q) => (
              <Chip key={q.label} variant="filter" label={q.label} selected={why === q.body} onClick={() => setWhy(q.body)} />
            ))}
          </div>
        )}
        {needsReason && (
          <Input
            density="admin"
            label={typeof reason === 'string' ? reason : 'Reason (required, audited)'}
            value={why}
            onChange={setWhy}
            placeholder="Why this is being done"
          />
        )}
        <Input
          density="admin"
          label={`Type ${phrase} to confirm`}
          value={typed}
          onChange={setTyped}
          placeholder={phrase}
        />
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          {who
            ? `Acting as ${who} · written to the audit log with your IP and scope`
            : 'Written to the audit log with your IP and scope'}
        </span>
      </div>
    </Modal>
  );
}

/* ────────────────────────────  DiffView  ──────────────────────────── */

export type DiffRow = readonly [label: string, before: ReactNode, after: ReactNode];

/** Before/after for prices, settings and SEO templates. Changed rows tint; unchanged stay quiet. */
export function DiffView({
  rows,
  labels = ['Live', 'Draft'],
}: {
  rows: readonly DiffRow[];
  labels?: readonly [string, string] | readonly string[];
}) {
  return (
    <div style={{ border: '1px solid var(--c-border)', borderRadius: 'var(--r-tile)', overflow: 'hidden' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          background: 'var(--c-sunk)',
          fontSize: 'var(--fs-admin-colhead)',
          color: 'var(--c-ink3)',
          padding: '6px var(--sp-md)',
        }}
      >
        <span>Field</span>
        <span style={{ textAlign: 'right' }}>{labels[0]}</span>
        <span style={{ textAlign: 'right' }}>{labels[1]}</span>
      </div>
      {rows.map(([label, before, after]) => {
        const changed = before !== after;
        return (
          <div
            key={label}
            data-changed={changed || undefined}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr 1fr',
              alignItems: 'center',
              padding: '7px var(--sp-md)',
              borderTop: '1px solid var(--c-border)',
              fontSize: 'var(--fs-admin-cell)',
              background: changed ? 'var(--c-saffron-50)' : 'transparent',
            }}
          >
            <span style={{ color: 'var(--c-ink2)' }}>{label}</span>
            <span
              className="tabular"
              style={{ textAlign: 'right', color: 'var(--c-ink3)', textDecoration: changed ? 'line-through' : 'none' }}
            >
              {before}
            </span>
            <span
              className="tabular"
              style={{
                textAlign: 'right',
                color: changed ? 'var(--c-saffron-text)' : 'var(--c-ink3)',
                fontWeight: changed ? 'var(--fw-semibold)' : 'var(--fw-regular)',
              }}
            >
              {after}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────────────  Meter  ──────────────────────────── */

/** A ceiling you can breach — the Pages page budget, an offer's spend cap, a redirect rule cap. */
export function Meter({ label, used, ceiling, note }: { label: string; used: number; ceiling: number; note?: string }) {
  const pct = Math.min(100, (used / ceiling) * 100);
  const tone = pct > 95 ? 'error' : pct > 85 ? 'warn' : 'ok';
  const colour = tone === 'error' ? 'var(--c-error)' : tone === 'warn' ? 'var(--c-warn)' : 'var(--c-success)';
  // The bar takes the accent (3:1 is enough for a fill); the figures and the note are text, so
  // they take the AA `*Text` variant of the same tone (brand doc 03 §10).
  const ink = tone === 'error' ? 'var(--c-error-text)' : 'var(--c-warn-text)';
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 320, flex: '1 1 320px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--sp-md)',
          fontSize: 'var(--fs-admin-colhead)',
          color: 'var(--c-ink3)',
        }}
      >
        <span style={{ minWidth: 0 }}>{label}</span>
        <span className="tabular" style={{ whiteSpace: 'nowrap', color: tone === 'ok' ? 'var(--c-ink2)' : ink }}>
          {used.toLocaleString('en-IN')} / {ceiling.toLocaleString('en-IN')}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={ceiling}
        aria-label={label}
        style={{ height: 6, borderRadius: 'var(--r-pill)', background: 'var(--c-sunk)', overflow: 'hidden' }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: colour }} />
      </div>
      {note && (
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: tone === 'ok' ? 'var(--c-ink3)' : ink }}>
          {note}
        </span>
      )}
    </div>
  );
}

/* ────────────────────────────  Sparkline  ──────────────────────────── */

/**
 * Composed from an inline SVG rather than a chart library. The design system ships no chart
 * component; adding one for a 90×28 trend line would be a new component family, which the brief
 * forbids. Flagged rather than smuggled in.
 */
export function Sparkline({
  points,
  tone,
  baseline = 'zero',
}: {
  points: readonly number[];
  tone?: 'warn' | 'error';
  /**
   * `zero` anchors the line at 0 — honest for a volume tile, where the height *is* the quantity.
   * `range` fits the series' own min–max, which is the only way a trend whose variation is small
   * next to its total shows any shape at all. It is a rescale, so a caller using it owes the
   * reader the numbers: pass the range in the label beside the line.
   */
  baseline?: 'zero' | 'range';
}) {
  const max = Math.max(...points, 1);
  const min = baseline === 'range' ? Math.min(...points) : 0;
  const span = Math.max(max - min, 1);
  const d = points
    .map((p, i) => `${(i / Math.max(1, points.length - 1)) * 100},${28 - ((p - min) / span) * 24}`)
    .join(' ');
  const colour = tone === 'error' ? 'var(--c-error)' : tone === 'warn' ? 'var(--c-warn)' : 'var(--c-info)';
  return (
    <svg
      viewBox="0 0 100 28"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ width: '100%', height: 28, display: 'block', color: colour }}
    >
      <polyline
        points={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ────────────────────────────  Wiring notice  ──────────────────────────── */

/**
 * Says, on the screen itself, that what you are looking at is not real.
 *
 * Thirty-eight of this console's screens still render fixture data, and until this existed nothing
 * on them said so. That is the failure this component exists to prevent: an operator reads a number
 * off a screen, acts on it, and nothing ever told them the number was invented. A banner is not a
 * substitute for wiring the screen — it is what honesty costs while the screen waits its turn.
 *
 * It is deliberately not dismissible. A notice you can close is a notice the second reader never
 * sees.
 */
export function WiringNotice({ slice, what }: { slice: string; what: string }) {
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-sm)',
        background: 'var(--c-warn-bg)',
        color: 'var(--c-warn-text)',
        border: '1px solid var(--c-warn)',
        borderRadius: 'var(--r-tile)',
        padding: 'var(--sp-md)',
        fontSize: 'var(--fs-admin-cell)',
      }}
    >
      <Icon name="triangle-alert" size={16} />
      <span style={{ flex: 1 }}>
        <strong style={{ fontWeight: 'var(--fw-semibold)' }}>Sample data — not this system.</strong>{' '}
        Everything below is a fixture built to the shape of the future API. It is here so the screen
        can be designed and reviewed; do not read a number off it and act. Waiting on backend slice{' '}
        <span className="mono-num">{slice}</span> — {what}.
      </span>
    </div>
  );
}

/** The narrower notice for a screen that is partly live. */
export function PartialWiringNotice({ note }: { note: string }) {
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-sm)',
        background: 'var(--c-surface2)',
        color: 'var(--c-ink2)',
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--r-tile)',
        padding: 'var(--sp-md)',
        fontSize: 'var(--fs-admin-cell)',
      }}
    >
      <Icon name="info" size={16} />
      <span style={{ flex: 1 }}>{note}</span>
    </div>
  );
}
